import React from 'react';
import { Link, Outlet, useSearchParams } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import { useAlertsApi } from '../../api';
import {
  DualPaneLayout, LeftPane, LeftPaneHeader, RightPane
} from '../../components/DualPaneLayout';
import { useAccountContext } from '../../contexts/AccountContext';
import { SetURLSearchParams } from '../../libs/useParentQuery';
import AlertList from './AlertsList';

interface NavItemProps {
  name: string;
  filter: string;
  icon: string;
  currentCasePath: string;
}

const NavItem: React.FC<NavItemProps> = (props) => {
  return (
    <Link key={props.name} to={`/alerts?filter=${props.filter}`}>
      <div className={`w-full text-lg rounded-md p-2 hover:bg-hover ${props.filter === props.currentCasePath ? 'bg-selected' : ''}`}>
        <i className={`bi ${props.icon}`} /> {props.name}
      </div>
    </Link>
  );
};

const OPEN_FILTER = 'state in ["active", "in_progress"]';
const RECENTLY_CLOSED_FILTER = '';

export const useAlertsQuery = (): [URLSearchParams, SetURLSearchParams] => {
  const [searchParams, setSearchParams] = useSearchParams();
  if (searchParams.has('alertsQuery')) {
    const decodedSearch = decodeURIComponent(searchParams.get('alertsQuery')!);
    return [new URLSearchParams(decodedSearch), () => {throw Error('Unsupported Set on Child Page'); }];
  }

  return [searchParams, setSearchParams];
}

const Alerts: React.FC = () => {
  const alertsApi = useAlertsApi();
  const accountContext = useAccountContext();

  const [search] = useAlertsQuery();

  const filter = search.get('filter') ?? OPEN_FILTER;

  const alertsQuery = useQuery(['alerts', accountContext.accountId, filter], async () => {
    const alerts = await alertsApi.query_alerts({
      queries: {
        filter
      }
    });

    return alerts;
  });

  return <>
    <DualPaneLayout>
      <LeftPane>
        <LeftPaneHeader title='Alerts' />
        <NavItem name='Open' filter={OPEN_FILTER} icon='' currentCasePath={filter} />
        <NavItem name='Recently Closed' filter={RECENTLY_CLOSED_FILTER} icon='' currentCasePath={filter} />
      </LeftPane>
      <RightPane>
        <div className='px-4 py-4 md:py-12'>
          {filter == OPEN_FILTER && <h2 className='text-3xl'>Open Alerts</h2>}
          {filter == RECENTLY_CLOSED_FILTER && <h2 className='text-2xl'>Recently Closed Alerts</h2>}
        </div>
        <AlertList alerts={alertsQuery.data} />
      </RightPane>
    </DualPaneLayout>
    <Outlet />
  </>
};

export default Alerts;